import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { RestaurantMenu as RedesignRestaurantMenu } from '../../../pages';
import * as Actions from '../../actions/Actions';
import * as Api from '../../Api';
import * as BasketControl from '../../BasketControl';
import * as Data from '../../Data';
import { basketSelectors, userActions } from '../../redux';
import * as Routes from '../../Routes';
import { menuSelectors } from '../../selectors';
import {
  getCollectionSummary,
  getDeliverySummary,
} from '../../selectors/menuSelectors';
import { flagService } from '../../services';
import { storage } from '../../services/storageServices';
import * as Store from '../../Store';
import { default as LegacyRestaurant } from './index.weborder';

const pageComponent = flagService.componentToggle(
  'uiRedesign',
  RedesignRestaurantMenu,
  LegacyRestaurant,
);

export type StateProps = ReturnType<typeof mapStateToProps>;
export type DispatchProps = ReturnType<typeof mapDispatchToProps>;
export type loadStatus = 'NOT_LOADED' | 'LOADING' | 'LOADED';

const mapStateToProps = (state: Store.State, ownProps: Routes.Routes) => {
  const { flipdish, deliveryOptions } = state;

  const targetedLoadingFlag = flagService.isFlagOn(state, 'targetedLoading');
  const restaurant = Routes.FindRestaurant(state, ownProps);

  let dl: Api.DeliveryLocation | null = null;
  if (
    ownProps.params.type == 'delivery' &&
    flipdish.DeliveryLocations.status == 'success'
  ) {
    dl =
      flipdish.DeliveryLocations.data.find(
        (d) => d.DeliveryLocationId == ownProps.params.id,
      ) || null;
  }

  let pickup = false;
  let delivery = false;

  let ContactFormType = Api.ContactFormType.None;

  if (flipdish.AppConfig.status == 'success') {
    const { data } = flipdish.AppConfig;
    pickup = data.PickupEnabled;
    delivery = data.DeliveryEnabled;
    ContactFormType = data.ContactFormType;
  }

  const { MenuData, CollectionSummaries } = flipdish;
  let menu = null;

  if (targetedLoadingFlag) {
    if (restaurant.status === 'success') {
      menu = menuSelectors.findMenuByUrl(
        { MenuData },
        { menuUrl: restaurant.data.MenuUrl },
      );
    }
  } else {
    menu =
      ownProps.params.type == 'delivery'
        ? menuSelectors.findDeliveryMenu(
            {
              deliveryOptions,
              MenuData,
            },
            {
              vrId: ownProps.params.vrid,
              deliveryLocationId: ownProps.params.id,
            },
          )
        : menuSelectors.findCollectionMenu(
            {
              CollectionSummaries,
              MenuData,
            },
            {
              vrId: ownProps.params.vrid,
              prId: ownProps.params.id,
            },
          );
  }

  let menuUrl = undefined;
  if (ownProps.params.type === 'collection') {
    const collectionSummary = getCollectionSummary(
      state.flipdish.CollectionSummaries,
      {
        vrId: ownProps.params.vrid,
        prId: ownProps.params.id,
      },
    );
    if (collectionSummary) {
      menuUrl = collectionSummary.MenuUrl;
    }
  } else {
    const deliverySummary = getDeliverySummary(state.deliveryOptions, {
      vrId: ownProps.params.vrid,
      deliveryLocationId: ownProps.params.id,
    });
    if (deliverySummary) {
      menuUrl = deliverySummary.MenuUrl;
    }
  }

  const basket = basketSelectors.getBasketBridge(state);

  return {
    AppConfig: flipdish.AppConfig,
    AppDesignScheme: state.appDesignScheme,
    Basket: basket,
    CollectionSummaries: flipdish.CollectionSummaries,
    ContactFormType,
    Customer: flipdish.Customer,
    DeliveryEnabled: delivery,
    DeliveryLocation: dl,
    DeliverySummaries: flipdish.DeliverySummaries,
    ExpandedState: flipdish.ExpandedStates[ownProps.params.vrid],
    ExpandedStates: flipdish.ExpandedStates,
    flagTargetedLoadingEnabled: targetedLoadingFlag,
    Intercom: state.intercom,
    isDark: flipdish.isDark,
    Languages: state.locale.languages,
    Location: flipdish.Location.geo,
    MapLoader: flipdish.Config.MapLoader,
    menu,
    Menus: flipdish.MenuData,
    MenuScroll: flipdish.MenuScrollY,
    MenuUrl: menuUrl,
    Offset: flipdish.Offset,
    PickupEnabled: pickup,
    Restaurant: restaurant,
    Restaurants: state.flipdish.CollectionSummaries,
    ShowRestaurantList: flipdish.Config.showRestaurantList,
    ToastMessages: flipdish.ToastMessages,
    translate: getTranslate(state.locale) as any,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<Store.State, {}, Action>,
) => ({
  AddToast: (message: string) => {
    dispatch(Actions.AddToast(message));
  },

  FindInitialRestaurant: (): any => {
    const action: any = Actions.FindInitialRestaurant() as any;
    dispatch(action);
  },

  getDataForAllRestaurants: () => {
    dispatch(Actions.getDataForAllRestaurants());
  },

  getMenuByUrl: (menuUrl: string) => {
    dispatch(Actions.getMenuByUrl(menuUrl));
  },

  getRestaurantData: (vrId: number, orderType: string, id: number) => {
    dispatch(Actions.getRestaurantData(vrId, orderType, id));
  },

  InitializeExpandedState: (vrid: number, expandedState: number[]) => {
    dispatch(Actions.InitializeExpandedState(vrid, expandedState));
  },

  loadCollectionDetails: (prId: number) => {
    dispatch(Actions.LoadCollectionDetails(prId, false));
  },

  loadDeliveryDetails: (prId: number, deliveryLocationId: number) => {
    dispatch(Actions.LoadDeliveryDetails(prId, deliveryLocationId, true));
  },

  LoadRestaurant: (routes: Routes.Routes) => {
    dispatch(Actions.LoadRestaurant(routes));
  },

  logoutSuccessBridge: () => {
    dispatch(userActions.logoutSuccessBridge());
  },

  PushLoginUrl: (url: string) => {
    dispatch(Actions.PushAfterLogin(url));
  },

  SetRestaurantUrl: () => {
    dispatch(Actions.SetRestaurantUrl(window.location.hash.substr(1)));
  },

  SetCustomer: (customer: Data.Async<Api.Customer>) => {
    dispatch({
      type: 'SET_CUSTOMER',
      payload: customer,
    });
    storage.removeItem('flipdish-customer');
  },

  AddItemToBasket: (payload: BasketControl.Item) => {
    dispatch(Actions.AddToBasket(payload));
  },
  RemoveItemFromBasket: (payload: {
    menuId: number;
    index: number;
    indexName?: string;
  }) => {
    dispatch(Actions.RemoveFromBasket(payload));
  },

  SetMenuScroll: (top: number) => {
    dispatch(Actions.SetMenuScroll(top));
  },

  SetSmsEnabled: (state: boolean) => {
    dispatch(Actions.SetSmsEnabled(state));
  },

  SetOptionsState: (
    options: Api.MenuItemOptionSetItem[],
    prevScreenOptions: Api.MenuItemOptionSetItem[][],
    current: number,
    prevIndices: number[],
  ) => {
    dispatch(
      Actions.SetOptionsState(options, prevScreenOptions, current, prevIndices),
    );
  },

  AddToExpandedState: (vrid: number, menuSectionId: number) => {
    dispatch(Actions.AddToExpandedState(vrid, menuSectionId));
  },

  RemoveFromExpandedState: (vrid: number, menuSectionId: number) => {
    dispatch(Actions.RemoveFromExpandedState(vrid, menuSectionId));
  },

  ChangeLanguage: (lang: string) => {
    dispatch(Actions.ChangeLanguage(lang));
  },

  UpdateCustomer: () => {
    dispatch(Actions.UpdateCustomer());
  },

  setUserName: (name: string) => {
    dispatch({
      type: 'SET_CUSTOMER_NAME',
      payload: {
        name,
      },
    });
  },

  ClearDeliveryLocations: () => {
    dispatch(Actions.ClearDeliveryLocations());
  },

  SetCustomerEmail: (customerEmail: string) => {
    dispatch(Actions.SetCustomerEmail(customerEmail));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(pageComponent);
